import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  VStack,
  Heading,
  Text,
  useToast,
  Badge,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Grid,
  GridItem,
  Tag,
  TagLeftIcon,
  TagLabel,
  Tooltip,
  Image,
} from '@chakra-ui/react';
import {
  Send,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  List,
  Youtube,
  Globe,
  HelpCircle,
  FileText,
  Sparkles,
} from 'lucide-react';
import { submitContent, detectInputType, listJobs, getBillingUsage } from '../lib/api';
import { supabase } from '../lib/supabase';

const INPUT_TYPE_META: Record<
  string,
  { label: string; color: string; icon: typeof Youtube }
> = {
  youtube: { label: 'YouTube Video', color: 'red', icon: Youtube },
  general_topic: { label: 'General Topic', color: 'blue', icon: FileText },
  atp_question: { label: 'Question (ATP)', color: 'purple', icon: HelpCircle },
  competitor_url: { label: 'Competitor URL', color: 'orange', icon: Globe },
};

const PHASE_LABELS: Record<string, string> = {
  detecting_input: 'Analyzing your input...',
  researching_keywords: 'Researching keywords...',
  gathering_research: 'Gathering research from multiple sources...',
  generating_content: 'Writing your article...',
  optimizing_title: 'Crafting the title...',
  generating_images: 'Creating images...',
  generating_schema: 'Building SEO metadata...',
  validating_quality: 'Running quality checks...',
  creating_draft: 'Preparing WordPress draft...',
  running_rag_pipeline: 'Processing YouTube video...',
  finalizing: 'Wrapping up...',
};

interface Job {
  id: string;
  video_id: string | null;
  youtube_url: string | null;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  created_at: string;
  completed_at?: string;
  input_type?: string;
  site_id?: string;
  input_text?: string;
  quality_score?: number;
  current_phase?: string | null;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'queued':
      return 'yellow';
    case 'processing':
      return 'orange';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'queued':
      return <Clock size={14} />;
    case 'processing':
      return <Loader2 size={14} />;
    case 'completed':
      return <CheckCircle size={14} />;
    case 'failed':
      return <XCircle size={14} />;
    default:
      return null;
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

function truncateText(text: string, max = 60): string {
  if (!text || text.length <= max) return text || '';
  return text.slice(0, max) + '…';
}

interface SubmitContentProps {
  siteId: string;
}

interface UsageInfo {
  posts_used: number;
  posts_included: number;
  posts_remaining: number;
  overage_posts: number;
  overage_rate_cents: number;
  subscription_status: string;
  requires_overage_confirmation: boolean;
}

export default function SubmitContent({ siteId }: SubmitContentProps) {
  const [inputText, setInputText] = useState('');
  const [detectedType, setDetectedType] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const toast = useToast();

  useEffect(() => {
    getBillingUsage()
      .then(data => setUsage(data))
      .catch(() => setUsage(null));
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const data = await listJobs(10, siteId);
      setJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000);

    const channel = supabase
      .channel(`jobs-realtime-${siteId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'jobs', filter: `site_id=eq.${siteId}` },
        (payload) => {
          const updated = payload.new as Record<string, unknown>;
          setJobs((prev) =>
            prev.map((j) =>
              j.id === updated.id
                ? {
                    ...j,
                    status: (updated.status as Job['status']) ?? j.status,
                    current_phase: (updated.current_phase as string | null) ?? null,
                    quality_score: (updated.quality_score as number | undefined) ?? j.quality_score,
                    error_message: (updated.error_message as string | undefined) ?? j.error_message,
                    completed_at: (updated.completed_at as string | undefined) ?? j.completed_at,
                  }
                : j,
            ),
          );
        },
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchJobs]);

  useEffect(() => {
    if (!inputText.trim()) {
      setDetectedType(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const type = await detectInputType(inputText.trim());
        setDetectedType(type);
      } catch {
        setDetectedType(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [inputText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputText.trim()) {
      toast({
        title: 'Input Required',
        description: 'Please enter a YouTube URL, topic, question, or competitor URL.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await submitContent(inputText.trim(), siteId);

      const optimisticJob: Job = {
        id: data.job_id,
        video_id: data.video_id || null,
        youtube_url: null,
        status: 'queued',
        created_at: new Date().toISOString(),
        input_type: data.input_type,
        site_id: siteId,
        input_text: inputText.trim(),
        current_phase: null,
      };
      setJobs((prev) => [optimisticJob, ...prev]);

      toast({
        title: 'Job Submitted!',
        description: `${INPUT_TYPE_META[data.input_type]?.label || 'Content'} is being processed. Check back in a few minutes.`,
        status: 'success',
        duration: 5000,
      });
      setInputText('');
      setDetectedType(null);
    } catch (error: any) {
      toast({
        title: 'Submission Failed',
        description: error?.response?.detail || error?.message || 'Unknown error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeMeta = detectedType ? INPUT_TYPE_META[detectedType] : null;
  const TypeIcon = typeMeta?.icon || Sparkles;

  return (
    <Grid templateColumns="repeat(12, 1fr)" gap={8}>
      {/* Submit Form */}
      <GridItem colSpan={{ base: 12, lg: 7 }}>
        <Box
          bg="white"
          p={{ base: 6, md: 8 }}
          borderRadius="xl"
          boxShadow="sm"
          borderWidth={1}
          borderColor="gray.200"
        >
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Box color="brand.500">
                <Sparkles size={32} strokeWidth={2} />
              </Box>
              <Heading size="lg" color="gray.900">
                Generate Content
              </Heading>
            </HStack>

            <Text color="gray.600">
              Enter a YouTube URL, general topic, question, or competitor article
              URL. The system will auto-detect the input type and generate an
              SEO-optimized blog post.
            </Text>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Content Input
                  </FormLabel>
                  <Textarea
                    placeholder={
                      'Paste a YouTube URL, enter a topic, ask a question, or paste a competitor URL…\n\nExamples:\n• https://youtube.com/watch?v=...\n• How to improve your credit score in 2026\n• What is the difference between FICO and VantageScore?\n• https://competitor-blog.com/article-to-beat'
                    }
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    size="lg"
                    fontSize="sm"
                    minH="210px"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{
                      borderColor: 'brand.400',
                      boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                    }}
                  />
                </FormControl>

                {typeMeta && (
                  <HStack>
                    <Text fontSize="sm" color="gray.500">
                      Detected:
                    </Text>
                    <Tag
                      size="md"
                      colorScheme={typeMeta.color}
                      variant="subtle"
                      borderRadius="full"
                    >
                      <TagLeftIcon as={() => <TypeIcon size={14} />} />
                      <TagLabel>{typeMeta.label}</TagLabel>
                    </Tag>
                  </HStack>
                )}

                {usage && usage.requires_overage_confirmation && (
                  <Alert
                    status="warning"
                    borderRadius="lg"
                    bg="orange.50"
                    borderWidth={1}
                    borderColor="orange.200"
                  >
                    <AlertIcon color="orange.500" />
                    <Box>
                      <Text color="orange.800" fontSize="sm" fontWeight="600">
                        You've used all {usage.posts_included} included posts
                      </Text>
                      <Text color="orange.700" fontSize="xs">
                        This post will be billed at ${(usage.overage_rate_cents / 100).toFixed(2)}/post.
                        {' '}You've used {usage.overage_posts} overage post{usage.overage_posts !== 1 ? 's' : ''} so far.
                      </Text>
                    </Box>
                  </Alert>
                )}

                {usage && usage.posts_included > 0 && !usage.requires_overage_confirmation && (
                  <Text fontSize="xs" color="gray.500">
                    {usage.posts_remaining} of {usage.posts_included} included posts remaining this period
                  </Text>
                )}

                <Button
                  type="submit"
                  size="lg"
                  bg="brand.400"
                  color="gray.900"
                  fontWeight="semibold"
                  isLoading={isSubmitting}
                  loadingText="Submitting..."
                  leftIcon={<Send size={18} />}
                  _hover={{
                    bg: 'brand.500',
                    transform: 'translateY(-2px)',
                    shadow: 'lg',
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s"
                >
                  Generate Blog Post
                </Button>
              </VStack>
            </form>

            <Alert
              status="info"
              borderRadius="lg"
              bg="brand.50"
              borderWidth={1}
              borderColor="brand.200"
            >
              <AlertIcon color="brand.600" />
              <Text color="brand.800" fontSize="sm">
                Processing typically takes 3-5 minutes. The post will appear in
                the Review tab when ready.
              </Text>
            </Alert>
          </VStack>
        </Box>
      </GridItem>

      {/* Recent Jobs */}
      <GridItem colSpan={{ base: 12, lg: 5 }}>
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          borderWidth={1}
          borderColor="gray.200"
        >
          <Box p={6}>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Box color="gray.400">
                    <List size={20} />
                  </Box>
                  <Heading size="md" color="gray.900">
                    Recent Jobs
                  </Heading>
                </HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchJobs}
                  leftIcon={<RefreshCw size={14} />}
                  color="brand.600"
                  _hover={{ color: 'brand.700', bg: 'brand.50' }}
                >
                  Refresh
                </Button>
              </HStack>

              <Divider />

              {isLoadingJobs ? (
                <HStack justify="center" py={4}>
                  <Spinner size="sm" color="brand.500" />
                  <Text fontSize="sm" color="gray.600">
                    Loading jobs...
                  </Text>
                </HStack>
              ) : jobs.length === 0 ? (
                <Text
                  color="gray.500"
                  textAlign="center"
                  py={8}
                  fontSize="sm"
                >
                  No jobs yet. Submit content to get started!
                </Text>
              ) : (
                <VStack
                  spacing={2}
                  align="stretch"
                  maxH="500px"
                  overflowY="auto"
                >
                  {jobs.map((job) => {
                    const meta = INPUT_TYPE_META[job.input_type || 'general_topic'];
                    return (
                      <Box
                        key={job.id}
                        p={3}
                        borderWidth={1}
                        borderRadius="lg"
                        borderColor="gray.200"
                        _hover={{
                          borderColor: 'brand.300',
                          bg: 'brand.50',
                          shadow: 'sm',
                        }}
                        transition="all 0.2s"
                      >
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            {meta && (
                              <HStack spacing={1}>
                                {job.input_type === 'youtube' && (
                                  <Image
                                    src="/images/youtube-logo-only.svg"
                                    alt="YouTube"
                                    boxSize="14px"
                                    flexShrink={0}
                                  />
                                )}
                                <Badge
                                  colorScheme={meta.color}
                                  fontSize="2xs"
                                  borderRadius="full"
                                >
                                  {meta.label}
                                </Badge>
                              </HStack>
                            )}
                            {job.quality_score != null && (
                              <Badge
                                colorScheme={
                                  job.quality_score >= 80
                                    ? 'green'
                                    : job.quality_score >= 60
                                      ? 'yellow'
                                      : 'red'
                                }
                                fontSize="2xs"
                                borderRadius="full"
                              >
                                Q: {job.quality_score}
                              </Badge>
                            )}
                          </HStack>
                          <Badge
                            colorScheme={getStatusColor(job.status)}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            {getStatusIcon(job.status)}
                            <span style={{ marginLeft: '4px' }}>
                              {job.status}
                            </span>
                          </Badge>
                        </HStack>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color="gray.900"
                          noOfLines={1}
                        >
                          {truncateText(job.input_text || job.youtube_url || job.video_id || '')}
                        </Text>
                        <Text fontSize="xs" color="gray.400" mt={1}>
                          {formatDate(job.created_at)}
                        </Text>
                        {job.status === 'processing' && job.current_phase && (
                          <HStack mt={2} spacing={2}>
                            <Spinner size="xs" color="orange.400" speed="0.8s" />
                            <Text fontSize="xs" color="orange.600" fontWeight="medium">
                              {PHASE_LABELS[job.current_phase] || job.current_phase}
                            </Text>
                          </HStack>
                        )}
                        {job.status === 'processing' && !job.current_phase && (
                          <HStack mt={2} spacing={2}>
                            <Spinner size="xs" color="orange.400" speed="0.8s" />
                            <Text fontSize="xs" color="orange.600" fontWeight="medium">
                              Starting up...
                            </Text>
                          </HStack>
                        )}
                        {job.error_message && (
                          <Tooltip
                            label={job.error_message}
                            fontSize="xs"
                            maxW="400px"
                            placement="bottom-start"
                            hasArrow
                          >
                            <Text
                              fontSize="xs"
                              color="red.500"
                              mt={2}
                              noOfLines={3}
                              cursor="help"
                            >
                              Error: {job.error_message}
                            </Text>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </VStack>
          </Box>
        </Box>
      </GridItem>
    </Grid>
  );
}
